import { useEffect, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import SellOutlinedIcon from "@mui/icons-material/SellOutlined";
import {
  Box,
  Dialog,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { normalizeString } from "helpers/normalizeString";
import type { IMarca } from "services/lojaMarcas";
import {
  fieldSx,
  modalHeaderSx,
  modalPaperSx,
  saveButtonSx,
  switchSx,
} from "../marcas.theme";

type Props = {
  open: boolean;
  editing: IMarca | null;
  saving: boolean;
  onClose: () => void;
  onSave: (payload: { nome: string; ativo: boolean }) => void;
};

export default function MarcaFormDialog({
  open,
  editing,
  saving,
  onClose,
  onSave,
}: Props) {
  const [nome, setNome] = useState("");
  const [ativo, setAtivo] = useState(true);

  useEffect(() => {
    if (!open) return;
    setNome(editing?.nome ?? "");
    setAtivo(editing?.ativo ?? true);
  }, [open, editing]);

  const slugPreview = nome.trim() ? normalizeString(nome) : "";

  const handleSave = () => {
    if (!nome.trim()) return;
    onSave({ nome: nome.trim(), ativo });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      PaperProps={{ sx: modalPaperSx }}
    >
      <Box sx={modalHeaderSx}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
          <Stack direction="row" spacing={2} alignItems="center" minWidth={0}>
            <SellOutlinedIcon sx={{ fontSize: 36, opacity: 0.9 }} />
            <Box minWidth={0}>
              <Typography variant="overline" sx={{ opacity: 0.75, letterSpacing: 1.2 }}>
                Catálogo da loja
              </Typography>
              <Typography variant="h5" fontWeight="bold" noWrap>
                {editing ? "Editar marca" : "Nova marca"}
              </Typography>
            </Box>
          </Stack>
          <IconButton onClick={onClose} sx={{ color: "inherit" }} aria-label="Fechar">
            <CloseIcon />
          </IconButton>
        </Stack>
      </Box>

      <MDBox sx={{ p: 3 }}>
        <Stack spacing={2.5}>
          <TextField
            label="Nome da marca"
            value={nome}
            onChange={(e) => setNome(e.target.value)}
            placeholder="Ex.: Nike, Adidas, Oakley…"
            fullWidth
            size="small"
            sx={fieldSx}
            autoFocus
          />
          {slugPreview ? (
            <MDTypography variant="caption" color="text.secondary">
              Slug gerado: <strong>{slugPreview}</strong>
            </MDTypography>
          ) : null}

          <FormControlLabel
            control={
              <Switch
                checked={ativo}
                onChange={(e) => setAtivo(e.target.checked)}
                sx={switchSx}
              />
            }
            label={
              <MDTypography variant="body2" fontWeight="medium">
                {ativo ? "Marca ativa" : "Marca inativa"}
              </MDTypography>
            }
          />
          <MDTypography variant="caption" color="text.secondary">
            Marcas inativas não aparecem na seleção ao cadastrar produtos.
          </MDTypography>
        </Stack>
      </MDBox>

      <MDBox
        px={3}
        py={2}
        display="flex"
        justifyContent="flex-end"
        gap={1}
        borderTop="1px solid"
        borderColor="grey.200"
      >
        <MDButton variant="outlined" color="dark" onClick={onClose}>
          Cancelar
        </MDButton>
        <MDButton
          variant="gradient"
          color="dark"
          onClick={handleSave}
          disabled={saving || !nome.trim()}
          sx={saveButtonSx}
        >
          {saving ? "Salvando…" : "Salvar marca"}
        </MDButton>
      </MDBox>
    </Dialog>
  );
}
